<template>
  <el-config-provider :z-index="overlayPopperZIndex">
    <div v-if="modelValue" class="bluemap-overlay-wrapper">
      <transition name="overlay-fade">
        <div v-show="!collapsed" class="bluemap-overlay">
          <iframe
            ref="iframeRef"
            class="bluemap-frame"
            :key="iframeKey"
            :src="currentMapUrl"
            allow="clipboard-read; clipboard-write"
            referrerpolicy="no-referrer"
            frameborder="0"
            allowfullscreen
          ></iframe>

          <div class="overlay-top-center-actions">
            <el-tooltip content="返回上一页" placement="bottom">
              <button type="button" class="overlay-center-btn back-btn" @click="handleClose">
                <el-icon><Close /></el-icon>
                <span>关闭</span>
              </button>
            </el-tooltip>
            <el-tooltip content="收起地图" placement="bottom">
              <button type="button" class="overlay-center-btn collapse-btn" @click="handleCollapse">
                <el-icon><Fold /></el-icon>
                <span>收起</span>
              </button>
            </el-tooltip>
          </div>

          <div v-if="canManage" class="overlay-toolbar">
            <el-tooltip v-if="canAddStation" content="添加车站" placement="left">
              <button type="button" class="toolbar-btn station-btn" @click="openStationPanel">
                <el-icon><LocationInformation /></el-icon>
                <span>添加车站</span>
              </button>
            </el-tooltip>
            <el-tooltip v-if="canAddBuilding" content="添加建筑" placement="left">
              <button type="button" class="toolbar-btn building-btn" @click="openBuildingPanel">
                <el-icon><OfficeBuilding /></el-icon>
                <span>添加建筑</span>
              </button>
            </el-tooltip>
            <el-tooltip v-if="canAddRegion" content="添加行政区规划" placement="left">
              <button type="button" class="toolbar-btn region-btn" @click="openRegionPanel">
                <el-icon><Compass /></el-icon>
                <span>添加行政区</span>
              </button>
            </el-tooltip>
          </div>

          <DraggablePanel
            v-model="stationPanelVisible"
            title="新增车站"
            :initial-width="panelSizes.station.width"
            :initial-height="panelSizes.station.height"
            :initial-top="getPanelInitialPosition('station').top"
            :initial-left="getPanelInitialPosition('station').left"
            :min-width="640"
            :min-height="520"
            :z-index="stationPanelZ"
            @activate="bringPanelToFront('station')"
          >
            <StationForm
              :lines="lines"
              :regions="regions"
              :default-quick-add="false"
              @success="handleStationSaved"
              @cancel="stationPanelVisible = false"
            />
          </DraggablePanel>

          <DraggablePanel
            v-model="buildingPanelVisible"
            title="新增建筑"
            :initial-width="panelSizes.building.width"
            :initial-height="panelSizes.building.height"
            :initial-top="getPanelInitialPosition('building').top"
            :initial-left="getPanelInitialPosition('building').left"
            :min-width="680"
            :min-height="560"
            :z-index="buildingPanelZ"
            @activate="bringPanelToFront('building')"
          >
            <BuildingForm
              :regions="regions"
              :default-region-id="defaultRegionId"
              @success="handleBuildingSaved"
              @cancel="buildingPanelVisible = false"
            />
          </DraggablePanel>

          <DraggablePanel
            v-model="regionShapePanelVisible"
            title="行政区规划"
            :initial-width="panelSizes.region.width"
            :initial-height="panelSizes.region.height"
            :initial-top="getPanelInitialPosition('region').top"
            :initial-left="getPanelInitialPosition('region').left"
            :min-width="640"
            :min-height="480"
            :z-index="regionPanelZ"
            @activate="bringPanelToFront('region')"
          >
            <RegionShapeForm
              :regions="regions"
              :default-region-id="defaultRegionId"
              @success="handleRegionShapeSaved"
              @cancel="regionShapePanelVisible = false"
            />
          </DraggablePanel>
        </div>
      </transition>

      <transition name="overlay-fade">
        <div v-show="collapsed" class="collapsed-restore-container">
          <el-tooltip content="恢复地图" placement="bottom">
            <button
              type="button"
              class="overlay-center-btn restore-btn"
              @click="handleRestore"
            >
              <el-icon><MapLocation /></el-icon>
              <span>恢复地图</span>
            </button>
          </el-tooltip>
        </div>
      </transition>
    </div>
  </el-config-provider>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import {
  Fold,
  LocationInformation,
  OfficeBuilding,
  MapLocation,
  Close,
  Compass
} from '@element-plus/icons-vue'
import { lineApi, buildingRegionApi, exportApi } from '../api'
import configService from '../services/configService'
import { useAuth } from '../composables/useAuth'
import StationForm from './StationForm.vue'
import BuildingForm from './BuildingForm.vue'
import DraggablePanel from './DraggablePanel.vue'
import RegionShapeForm from './RegionShapeForm.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'station-saved', 'building-saved', 'region-shape-saved'])

const collapsed = ref(false)
const iframeKey = ref(0)
const iframeRef = ref(null)
const stationPanelVisible = ref(false)
const buildingPanelVisible = ref(false)
const regionShapePanelVisible = ref(false)
const savedMapUrl = ref('https://map.rofc.online/')
const currentMapUrl = ref('https://map.rofc.online/')
const lines = ref([])
const regions = ref([])
const defaultRegionId = ref(null)
const linesLoaded = ref(false)
const regionsLoaded = ref(false)
const overlayPopperZIndex = 6200
const panelSizes = {
  station: { width: 720, height: 480 },
  building: { width: 720, height: 480 },
  region: { width: 720, height: 540 }
}
const nextZIndex = ref(5200)
const stationPanelZ = ref(5200)
const buildingPanelZ = ref(5199)
const regionPanelZ = ref(5198)
const pushingBlueMap = ref(false)

const { isGuest, isLoggedIn, hasPermission } = useAuth()
const canAddStation = computed(() => hasPermission(['station.create']))
const canAddBuilding = computed(() => hasPermission(['building.create']))
const canAddRegion = computed(() => hasPermission(['region_shape.update']))
const canManage = computed(() => isLoggedIn.value && !isGuest.value && (canAddStation.value || canAddBuilding.value || canAddRegion.value))

// 计算面板的初始位置（右下角）
const getPanelInitialPosition = (panelType) => {
  const size = panelSizes[panelType]
  return {
    top: window.innerHeight - size.height - 80,
    left: window.innerWidth - size.width - 80
  }
}

const visible = computed(() => props.modelValue)

watch(visible, async (value) => {
  if (value) {
    collapsed.value = false
    await preloadData()
  } else {
    stationPanelVisible.value = false
    buildingPanelVisible.value = false
    regionShapePanelVisible.value = false
  }
})

const preloadData = async () => {
  if (!canManage.value) {
    return
  }
  if (canAddStation.value) {
    await loadLines()
  }
  if (canAddBuilding.value || canAddRegion.value) {
    await loadRegions()
  }
}

const loadLines = async (force = false) => {
  if (linesLoaded.value && !force) {
    return
  }
  try {
    const result = await lineApi.getAll()
    lines.value = result.data || []
    linesLoaded.value = true
  } catch (error) {
    ElMessage.error('加载线路列表失败')
    console.error('加载线路失败', error)
  }
}

const loadRegions = async (force = false) => {
  if (regionsLoaded.value && !force) {
    return
  }
  try {
    const result = await buildingRegionApi.getAll()
    regions.value = result.data || []
    defaultRegionId.value = regions.value[0]?.id ?? null
    regionsLoaded.value = true
  } catch (error) {
    ElMessage.error('加载行政区域失败')
    console.error('加载行政区域失败', error)
  }
}

const bringPanelToFront = (panel) => {
  nextZIndex.value += 1
  if (panel === 'station') {
    stationPanelZ.value = nextZIndex.value
  } else if (panel === 'building') {
    buildingPanelZ.value = nextZIndex.value
  } else if (panel === 'region') {
    regionPanelZ.value = nextZIndex.value
  }
}

const reloadMap = () => {
  iframeKey.value += 1
}

// 静默推送到 BlueMap
const syncToBlueMap = async () => {
  if (pushingBlueMap.value) return
  pushingBlueMap.value = true
  try {
    const apiConfig = configService.getApiConfig()
    await exportApi.pushToBlueMap({
      include_config_header: true,
      ...apiConfig
    })
    console.log('✅ 行政区保存后已自动推送 BlueMap')
  } catch (error) {
    console.warn('⚠️ 行政区保存后自动推送 BlueMap 失败（不阻塞主流程）:', error?.message || error)
  } finally {
    pushingBlueMap.value = false
  }
}

// 尝试获取iframe当前的完整URL（包括hash）
const getCurrentIframeUrl = () => {
  try {
    if (iframeRef.value) {
      // 方法1: 尝试直接访问（同源情况下有效）
      try {
        const url = iframeRef.value.contentWindow.location.href
        if (url && url !== 'about:blank') {
          return url
        }
      } catch (e) {
        // 跨域限制，尝试其他方法
      }
      
      // 方法2: 获取src属性（可能不包含用户导航后的hash变化）
      const src = iframeRef.value.src
      if (src && src !== 'about:blank') {
        return src
      }
    }
  } catch (error) {
    console.error('获取iframe URL时出错:', error)
  }
  return null
}

// 保存当前地图URL
const saveCurrentMapUrl = () => {
  const url = getCurrentIframeUrl()
  if (url) {
    savedMapUrl.value = url
    console.log('已保存地图URL:', url)
  } else {
    // 保存当前设置的URL
    savedMapUrl.value = currentMapUrl.value
    console.log('使用当前设置的URL:', savedMapUrl.value)
  }
}

// 恢复地图URL
const restoreMapUrl = () => {
  if (savedMapUrl.value && savedMapUrl.value !== currentMapUrl.value) {
    // 如果保存的URL与当前不同，更新URL并强制刷新
    currentMapUrl.value = savedMapUrl.value
    // 增加key强制iframe重新加载
    iframeKey.value += 1
    console.log('恢复地图到:', savedMapUrl.value)
  }
  collapsed.value = false
}

// 处理收起地图
const handleCollapse = () => {
  saveCurrentMapUrl()
  collapsed.value = true
}

// 处理恢复地图
const handleRestore = () => {
  restoreMapUrl()
}

const closePanels = () => {
  stationPanelVisible.value = false
  buildingPanelVisible.value = false
  regionShapePanelVisible.value = false
}

const handleClose = () => {
  collapsed.value = false
  closePanels()
  emit('update:modelValue', false)
}

const openStationPanel = async () => {
  if (!canAddStation.value) {
    ElMessage.warning('无权添加车站')
    return
  }
  await loadLines(true)
  stationPanelVisible.value = true
  bringPanelToFront('station')
}

const openBuildingPanel = async () => {
  if (!canAddBuilding.value) {
    ElMessage.warning('无权添加建筑')
    return
  }
  await loadRegions(true)
  buildingPanelVisible.value = true
  bringPanelToFront('building')
}

const openRegionPanel = async () => {
  if (!canAddRegion.value) {
    ElMessage.warning('无权添加行政区')
    return
  }
  await loadRegions(true)
  regionShapePanelVisible.value = true
  bringPanelToFront('region')
}

const handleStationSaved = () => {
  stationPanelVisible.value = false
  reloadMap()
  emit('station-saved')
}

const handleBuildingSaved = () => {
  buildingPanelVisible.value = false
  reloadMap()
  emit('building-saved')
}

const handleRegionShapeSaved = () => {
  regionShapePanelVisible.value = false
  syncToBlueMap()
  reloadMap()
  emit('region-shape-saved')
}

watch(collapsed, (value) => {
  if (!value) {
    reloadMap()
  }
})

onMounted(() => {
  if (visible.value) {
    preloadData()
  }
})

// 暴露给父组件的方法和状态
defineExpose({
  collapsed,
  restoreMap: () => {
    restoreMapUrl()
  },
  canAddStation,
  canAddBuilding,
  canAddRegion
})
</script>

<style scoped>
.bluemap-overlay-wrapper {
  position: fixed;
  inset: 0;
  z-index: 5000;
  pointer-events: none;
}

.bluemap-overlay {
  position: absolute;
  inset: 0;
  background: #0b1120;
  pointer-events: auto;
  overflow: hidden;
}

.bluemap-frame {
  width: 100%;
  height: 100%;
  border: 0;
}

.overlay-top-center-actions {
  position: absolute;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 16px;
  z-index: 5100;
}

.overlay-center-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 24px;
  border: none;
  color: #fff;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(8px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.back-btn {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.9), rgba(220, 38, 38, 0.9));
}

.back-btn:hover {
  background: linear-gradient(135deg, rgba(239, 68, 68, 1), rgba(220, 38, 38, 1));
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(239, 68, 68, 0.4);
}

.collapse-btn {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.9), rgba(37, 99, 235, 0.9));
}

.collapse-btn:hover {
  background: linear-gradient(135deg, rgba(59, 130, 246, 1), rgba(37, 99, 235, 1));
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(59, 130, 246, 0.4);
}

.overlay-toolbar {
  position: absolute;
  top: 32px;
  right: 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  z-index: 5100;
}

.toolbar-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 24px;
  border: none;
  color: #fff;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(8px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.station-btn {
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.9), rgba(147, 51, 234, 0.9));
}

.station-btn:hover {
  background: linear-gradient(135deg, rgba(168, 85, 247, 1), rgba(147, 51, 234, 1));
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(168, 85, 247, 0.4);
}

.building-btn {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.9), rgba(217, 119, 6, 0.9));
}

.building-btn:hover {
  background: linear-gradient(135deg, rgba(245, 158, 11, 1), rgba(217, 119, 6, 1));
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(245, 158, 11, 0.4);
}

.region-btn {
  background: linear-gradient(135deg, rgba(14, 165, 233, 0.9), rgba(2, 132, 199, 0.9));
}

.region-btn:hover {
  background: linear-gradient(135deg, rgba(14, 165, 233, 1), rgba(2, 132, 199, 1));
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(14, 165, 233, 0.4);
}

.collapsed-restore-container {
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5100;
  pointer-events: auto;
}

.restore-btn {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.9), rgba(22, 163, 74, 0.9));
}

.restore-btn:hover {
  background: linear-gradient(135deg, rgba(34, 197, 94, 1), rgba(22, 163, 74, 1));
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(34, 197, 94, 0.4);
}

.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
  transform: scale(0.98);
}
</style>
